import { NO_LIMIT_SEARCH_PARAMS, Query } from '@damofire/data-commons';
import { Asset, Column, System2d, Upright } from '../asset/asset.dto';
import { Inspection, InspectionResolutionStatus } from '../inspection/inspection.dto';
import { Issue, LocalizedRiskLevel, Resolution } from '../issue/issue.dto';
import { SystemView, SystemViewRequest, WarehouseSourceRef } from './warehouse-data-source';

/** Raw entities for one system, as fetched by a data source and before any joining. */
export interface SystemStateSlice {
  system: System2d;
  uprights: Upright[];
  columns: Column[];
  /** clientCode source, looked up by _id. Order is irrelevant. */
  assets: Asset[];
  /** Already scoped by the source (server query for current, buildSystemIssuesQuery for snapshot). */
  issues: Issue[];
}

/**
 * The one definition of "which issues belong to this view". Lifted verbatim from
 * SystemCanvasComponent.loadSystemData and UprightElevationCanvasComponent.loadIssues so both
 * the server-side query and the in-memory snapshot filter are built from the same rule.
 *
 * Note InspectionResolutionStatus.OPEN === 0, so this must stay an explicit === RESOLVED
 * comparison — a truthiness check would silently reclassify open inspections.
 */
export function applyIssueScopeFilters(q: Query, inspection?: Inspection): Query {
  if (inspection?.status?.status === InspectionResolutionStatus.RESOLVED) {
    q.filters['inspection._id'] = +inspection._id;
  } else {
    q.filters['resolution'] = Resolution.OPEN;
  }

  return q;
}

/**
 * Issue query for one system. Used against Firestore by the current source and against the
 * in-memory payload by the snapshot source via QueryService.searchCollection, which is the same
 * lodash filter engine the current read path already runs — so the two agree by construction.
 */
export function buildSystemIssuesQuery(systemNumber: number, inspection?: Inspection): Query {
  const q: Query = { filters: { 'asset.system': +systemNumber }, ...NO_LIMIT_SEARCH_PARAMS };

  return applyIssueScopeFilters(q, inspection);
}

export function buildAssetIssuesQuery(assetId: string | number, inspection?: Inspection): Query {
  const q: Query = {
    filters: { 'asset._id': +assetId },
    sort: { 'audit.createdDate': 'desc' },
    ...NO_LIMIT_SEARCH_PARAMS,
  };

  return applyIssueScopeFilters(q, inspection);
}

/**
 * Joins a slice into the view the canvas renders. Pure: never mutates the slice, because on the
 * snapshot path the arrays belong to the cached payload and a second render would see them sorted
 * or spliced.
 */
export function buildSystemView(slice: SystemStateSlice, req: SystemViewRequest, ref: WarehouseSourceRef, priorities: LocalizedRiskLevel[]): SystemView {
  const inspection = normalizeInspection(req.inspection);

  if (!slice.system) {
    return { system: null, uprights: [], columns: [], issues: [], columnDepths: [], priorities, inspection, source: ref };
  }

  const assetsById = new Map<string, Asset>((slice.assets ?? []).filter((asset) => !!asset).map((asset) => [String(asset._id), asset]));

  const uprights: Upright[] = [...(slice.uprights ?? [])]
    .sort((a: Upright, b: Upright): number => a.index - b.index)
    // Assigned unconditionally: the previous per-upright asset fetch also overwrote clientCode
    // with undefined when the asset was missing.
    .map((upright: Upright) => ({ ...upright, clientCode: assetsById.get(String(upright.assetId))?.clientCode }));

  const columns: Column[] = (slice.columns ?? []).map((column) => ({
    ...column,
    palletLevels: column.palletLevels ? [...column.palletLevels] : column.palletLevels,
  }));
  return {
    system: slice.system,
    uprights,
    columns,
    issues: req.includeIssues ? [...(slice.issues ?? [])] : [],
    columnDepths: buildColumnDepths(columns),
    priorities,
    inspection,
    source: ref,
  };
}

/** Distinct Column.rowIndex values in numeric order for the depth selector. */
function buildColumnDepths(columns: Column[]): number[] {
  const depths = columns.reduce((acc: number[], column: Column) => {
    if (!acc.includes(column.rowIndex)) {
      acc.push(column.rowIndex);
    }

    return acc;
  }, []);
  depths.sort((a, b) => a - b);

  return depths;
}

/** SystemCanvasDecorator.updateInspection dereferences reviews unguarded. */
export function normalizeInspection(inspection?: Inspection): Inspection {
  return inspection ? { ...inspection, reviews: inspection.reviews ?? [] } : inspection;
}
